import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { getMyReferrals, type InvestorReferral } from "@/lib/investor.functions";

export const Route = createFileRoute("/investor/deals")({
  head: () => ({
    meta: [
      { title: "Deals - Investor - DeckScore" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: InvestorDeals,
});

function scoreTone(s: number | null): string {
  if (s == null) return "text-ink-tertiary";
  if (s >= 75) return "text-[#1F7A3C]";
  if (s >= 55) return "text-[#B47B10]";
  return "text-[#C65C4E]";
}

function InvestorDeals() {
  const runGetReferrals = useServerFn(getMyReferrals);
  const [deals, setDeals] = useState<InvestorReferral[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { referrals } = await runGetReferrals();
        setDeals(referrals);
      } finally {
        setLoading(false);
      }
    })();
  }, [runGetReferrals]);

  if (loading) return <p className="text-ink-secondary">Loading…</p>;

  return (
    <>
      <div className="mb-8">
        <p className="font-script text-accent text-[22px] leading-none">dealflow</p>
        <h1 className="font-display text-[28px] font-semibold mt-1">Deals</h1>
        <p className="text-ink-secondary text-[14px] mt-1">
          Every deck referred through your dealflow page.
        </p>
      </div>

      {deals.length === 0 ? (
        <div className="bg-surface border border-line rounded-2xl p-10 text-center">
          <h2 className="font-display text-[20px] font-semibold">No deals yet</h2>
          <p className="text-ink-secondary text-[14px] mt-2 max-w-[420px] mx-auto">
            Share your dealflow page to start receiving founder decks.
          </p>
          <Link
            to="/investor/share"
            className="inline-flex mt-5 rounded-full bg-accent text-accent-ink px-5 py-2 text-[14px] font-bold"
          >
            Get your share kit →
          </Link>
        </div>
      ) : (
        <section className="bg-surface border border-line rounded-2xl p-6 md:p-8">
          <table className="w-full text-[14px]">
            <thead>
              <tr className="text-left text-ink-tertiary text-[12px] uppercase tracking-wide border-b border-line">
                <th className="py-2 pr-4 font-medium">Company</th>
                <th className="py-2 pr-4 font-medium">Score</th>
                <th className="py-2 pr-4 font-medium">Date</th>
                <th className="py-2 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {deals.map((r) => (
                <tr key={r.id} className="border-b border-line/60 last:border-0">
                  <td className="py-3 pr-4">
                    <Link
                      to="/investor/referrals/$deckId"
                      params={{ deckId: r.deck_id }}
                      className="text-ink hover:text-accent hover:underline font-medium"
                    >
                      {r.company}
                    </Link>
                  </td>
                  <td className={`py-3 pr-4 font-semibold ${scoreTone(r.overall_score)}`}>
                    {r.overall_score != null ? `${r.overall_score}/100` : "-"}
                  </td>
                  <td className="py-3 pr-4 text-ink-secondary">
                    {new Date(r.delivered_at).toLocaleDateString()}
                  </td>
                  <td className="py-3">
                    <Link
                      to="/investor/referrals/$deckId"
                      params={{ deckId: r.deck_id }}
                      className="text-accent hover:underline text-[13.5px]"
                    >
                      Open →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </>
  );
}
